// Widget loader IIFE — mounts a shadow-DOM launcher button on the host page.
// On click, lazy-mounts an iframe pointing at /widget/<appId>. Exposes
// window.Beacon(method, ...args) with a pre-ready queue.
//
// Host app snippet (anonymous mode):
//   <script>
//     (function(d,s,i,c){var j=d.createElement(s);
//     j.src='https://widget.<domain>/loader.js';
//     j.async=true;j.dataset.appId=i;j.dataset.config=c;
//     d.head.appendChild(j);})
//     (document,'script','<APP_ID>','{}');
//   </script>
//
// Secure Mode (visitor already signed in to the host site):
//   const idToken = await firebase.auth().currentUser.getIdToken();
//   window.Beacon('identify', { idToken, name: 'Alice' /* optional */ });
//   // The host's Firebase project ID must be in /apps/<appId>.trustedProjects[].
//   // identify can be called before or after Beacon('open') — if the iframe
//   // is already mounted, it reloads with the signed identity.

// The empty export makes this file an ES module, which is required for
// `declare global` augmentations to be valid.
export {};

declare global {
  interface Window {
    Beacon?: (method: string, ...args: unknown[]) => void;
  }
}

type BeaconMethod =
  | 'identify'
  | 'open'
  | 'close'
  | 'toggle'
  | 'navigate'
  | 'suggest'
  | 'event';

interface Config {
  appId: string;
  name: string;
  brandColor: string;
  logoUrl: string;
  docsUrl: string | null;
  chatEnabled: boolean;
}

// Production Cloud Run URL for the issueWidgetSignature callable. Same
// endpoint LfhHelpClient (iOS) hits — POSTs the host's Firebase ID token,
// gets back {email, sig, v} signed with the per-app HMAC secret.
const ISSUE_SIGNATURE_URL =
  'https://issuewidgetsignature-gllljoe5ga-uc.a.run.app';

interface SecureIdentity {
  email: string;
  sig: string;
  v: number;
  name?: string;
}

const script = document.currentScript as HTMLScriptElement | null;
const appId = script?.dataset.appId;
// The loader is served from the same origin as the widget iframe.
const widgetOrigin = new URL(script?.src ?? window.location.href).origin;

if (!appId) {
  console.warn('[lfh-widget] missing data-app-id on loader script');
} else {
  bootstrap(appId, widgetOrigin).catch((e) => {
    console.warn('[lfh-widget] bootstrap failed', e);
  });
}

async function bootstrap(appId: string, widgetOrigin: string): Promise<void> {
  // Drain any calls the host page made before the loader finished loading.
  // Two pre-load patterns are supported:
  //   (a) the snippet stub  window.Beacon = function(){(Beacon.q=Beacon.q||[]).push(arguments)};
  //       which collects raw IArguments into Beacon.q.
  //   (b) callers that wait until Beacon exists to call it (no early calls
  //       at all). Nothing to drain in that case.
  const preQueue: Array<[BeaconMethod, unknown[]]> = [];
  const stub = window.Beacon as
    | { q?: ArrayLike<IArguments> }
    | ((m: string, ...a: unknown[]) => void)
    | undefined;
  if (stub && typeof stub === 'function' && Array.isArray((stub as { q?: unknown }).q)) {
    // The function-typed branch of `stub` doesn't structurally overlap with
    // `{ q: IArguments[] }`, so TS requires a cast through unknown. The
    // runtime check above is what actually proves `.q` exists and is iterable.
    for (const argsLike of (stub as unknown as { q: IArguments[] }).q) {
      const argsArr = Array.from(argsLike);
      const [method, ...rest] = argsArr;
      preQueue.push([method as BeaconMethod, rest]);
    }
  }

  // Now queue any further calls until we've finished setup.
  const queue: Array<[BeaconMethod, unknown[]]> = preQueue.slice();
  window.Beacon = (method, ...args) => {
    queue.push([method as BeaconMethod, args]);
  };

  const config = await fetchConfig(appId, widgetOrigin);

  const host = document.createElement('div');
  host.style.cssText =
    'position:fixed;right:16px;bottom:16px;z-index:2147483647;';
  document.body.appendChild(host);
  const shadow = host.attachShadow({ mode: 'closed' });

  const style = document.createElement('style');
  style.textContent = `
    :host { all: initial; }
    .launcher {
      width: 56px; height: 56px; border-radius: 28px;
      background: ${sanitizeColor(config.brandColor)};
      box-shadow: 0 6px 20px rgba(0,0,0,.16);
      border: none; cursor: pointer; color: white;
      font: 600 18px system-ui, sans-serif;
      display: flex; align-items: center; justify-content: center;
    }
    .launcher:focus { outline: 2px solid #000; outline-offset: 2px; }
    .panel {
      position: fixed; right: 16px; bottom: 88px;
      width: 380px; height: min(620px, calc(100vh - 112px));
      background: white; border-radius: 12px; overflow: hidden;
      box-shadow: 0 16px 40px rgba(0,0,0,.18);
      display: none;
    }
    .panel.open { display: block; }
    iframe { width: 100%; height: 100%; border: 0; display: block; }
  `;
  shadow.appendChild(style);

  const launcher = document.createElement('button');
  launcher.className = 'launcher';
  launcher.setAttribute('aria-label', 'Open help widget');
  launcher.textContent = '?';
  shadow.appendChild(launcher);

  const panel = document.createElement('div');
  panel.className = 'panel';
  shadow.appendChild(panel);

  let iframe: HTMLIFrameElement | null = null;
  let open = false;
  let identity: SecureIdentity | null = null;

  function iframeURL(): string {
    const base = `${widgetOrigin}/widget/${encodeURIComponent(appId)}`;
    if (!identity) return base;
    const u = new URL(base);
    u.searchParams.set('email', identity.email);
    u.searchParams.set('sig', identity.sig);
    u.searchParams.set('v', String(identity.v));
    if (identity.name) u.searchParams.set('name', identity.name);
    return u.toString();
  }

  function setOpen(next: boolean): void {
    open = next;
    if (open) {
      if (!iframe) {
        iframe = document.createElement('iframe');
        iframe.src = iframeURL();
        iframe.setAttribute('title', `${config.name} help`);
        panel.appendChild(iframe);
      }
      panel.classList.add('open');
    } else {
      panel.classList.remove('open');
    }
  }

  launcher.addEventListener('click', () => setOpen(!open));

  // The iframe posts {type:'lfh:close'} when the visitor taps the X
  // inside the chat UI. Only honor messages from our own iframe so
  // unrelated postMessage chatter on the host page can't close us.
  window.addEventListener('message', (event) => {
    if (!iframe || event.source !== iframe.contentWindow) return;
    const data = event.data as { type?: string } | null;
    if (data?.type === 'lfh:close') setOpen(false);
  });

  // Replace the queueing Beacon with a live dispatcher.
  window.Beacon = (method, ...args) => {
    switch (method as BeaconMethod) {
      case 'open':
        setOpen(true);
        break;
      case 'close':
        setOpen(false);
        break;
      case 'toggle':
        setOpen(!open);
        break;
      case 'identify': {
        const payload = args[0] as
          | { idToken?: string; name?: string }
          | undefined;
        if (!payload?.idToken) {
          console.warn(
            '[lfh-widget] Beacon("identify") needs { idToken: string }',
          );
          break;
        }
        // Exchange the host's Firebase ID token for a Secure Mode sig.
        // Don't block the caller — fire-and-forget, then patch the iframe
        // URL when the response arrives. Failures fall through to anon.
        void issueSignature(appId, payload.idToken, payload.name)
          .then((id) => {
            identity = id;
            console.info('[lfh-widget] Secure Mode active');
            // If the iframe is already mounted, point it at the signed
            // URL. Reloading the src triggers the iframe's own Secure
            // Mode flow (mintToken → signInWithCustomToken → resume).
            if (iframe) iframe.src = iframeURL();
          })
          .catch((e) => {
            console.warn(
              '[lfh-widget] identify failed; staying anonymous',
              e,
            );
          });
        break;
      }
      default:
        // navigate, suggest, event — not yet implemented in MVP.
        console.debug('[lfh-widget] Beacon(%s) no-op in MVP', method, args);
    }
  };

  // Drain any calls made before we were ready.
  for (const [method, args] of queue) {
    window.Beacon(method, ...args);
  }
}

async function fetchConfig(appId: string, widgetOrigin: string): Promise<Config> {
  const r = await fetch(
    `${widgetOrigin}/api/widget/config?appId=${encodeURIComponent(appId)}`,
  );
  if (!r.ok) throw new Error(`config fetch failed: ${r.status}`);
  return (await r.json()) as Config;
}

async function issueSignature(
  appId: string,
  idToken: string,
  name: string | undefined,
): Promise<SecureIdentity> {
  const data: Record<string, string> = { appId, idToken };
  if (name) data.name = name;
  const r = await fetch(ISSUE_SIGNATURE_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ data }),
  });
  if (!r.ok) {
    const err = (await r.json().catch(() => ({}))) as {
      error?: { message?: string };
    };
    throw new Error(err.error?.message ?? `issueSignature HTTP ${r.status}`);
  }
  const env = (await r.json()) as { result: SecureIdentity };
  return env.result;
}

function sanitizeColor(input: string): string {
  // Accept hex, rgb(), rgba(), or named colors; block anything else.
  const s = (input ?? '').trim();
  if (/^#[0-9a-fA-F]{3,8}$/.test(s)) return s;
  if (/^rgba?\([\d.,\s%]+\)$/.test(s)) return s;
  if (/^[a-zA-Z]+$/.test(s)) return s;
  return '#0a0a0a';
}
